import { useState } from "react";
import { NewRecordDropdown } from "./NewRecordDropdown";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useDashboardConfig } from "@/hooks/useDashboardConfig";
import { usePermissions } from "@/hooks/usePermissions";
import { useAuth } from "@/contexts/AuthContext";
import { ConsolidatedGroupDashboard } from "@/components/Dashboard/ConsolidatedGroupDashboard";
import { DashboardGrid } from "./Widgets/DashboardGrid";
import { AddWidgetDialog } from "./Widgets/AddWidgetDialog";
import { 
  Plus, 
  RefreshCw,
  RotateCcw,
} from "lucide-react";
import { VisibilityToggle } from "@/components/ui/visibility-toggle";

export const DashboardView = () => {
  const { isMaster, canAccess } = usePermissions();
  const { activeCompanyId, isOrgMember, accessibleCompanyIds } = useAuth();
  const { 
    widgets, 
    isLoading, 
    isSaving,
    addWidget, 
    removeWidget, 
    reorderWidgets, 
    updateWidgetSize,
    resetToDefault 
  } = useDashboardConfig();
  
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);

  // Consolidado só com contexto explícito de grupo (Switcher) — nunca só por activeCompanyId null transitório
  const showConsolidatedGroup =
    isOrgMember &&
    accessibleCompanyIds.length > 1 &&
    activeCompanyId === null &&
    canAccess('org:view_group_finance');

  if (showConsolidatedGroup) {
    return (
      <div className="space-y-6 animate-fade-in">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Dashboard consolidado</h1>
            <p className="text-muted-foreground">Visão do grupo — todas as unidades</p>
          </div>
          <div className="flex items-center gap-3">
            <NewRecordDropdown
              onNavigate={(section, intent) => {
                if (typeof window !== 'undefined') {
                  const event = new CustomEvent('navigate-with-intent', {
                    detail: { section, intent },
                  });
                  window.dispatchEvent(event);
                }
              }}
            />
            {/* UI-HEADER-FIXO (I.160): sino e menu do usuário ficam na barra superior de todas as telas */}
          </div>
        </div>
        <ConsolidatedGroupDashboard />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-6 animate-fade-in">
        {/* Header Skeleton */}
        <div className="flex items-center justify-between">
          <div className="space-y-2">
            <Skeleton className="h-8 w-48" />
            <Skeleton className="h-4 w-64" />
          </div>
          <Skeleton className="h-10 w-32" />
        </div>
        
        {/* Grid Skeleton */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array(6).fill(0).map((_, i) => (
            <div key={i} className="p-6 rounded-lg border bg-card">
              <Skeleton className="h-4 w-24 mb-2" />
              <Skeleton className="h-8 w-16 mb-4" />
              <Skeleton className="h-4 w-20" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Dashboard</h1>
          <p className="text-muted-foreground">
            Visão geral personalizada do sistema
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Visibility Toggle - apenas para master */}
          {isMaster && <VisibilityToggle />}
          
          {/* Add Widget Button */}
          <Button 
            variant="outline"
            size="sm"
            onClick={() => setIsAddDialogOpen(true)}
            className="gap-2"
          >
            <Plus className="h-4 w-4" />
            Adicionar Indicador
          </Button>
          
          {/* Reset Button */}
          <Button 
            variant="ghost"
            size="sm"
            onClick={resetToDefault}
            disabled={isSaving}
            className="gap-2"
          >
            <RotateCcw className="h-4 w-4" />
            <span className="hidden sm:inline">Restaurar</span>
          </Button>
          
          {/* New Record */}
          <NewRecordDropdown onNavigate={(section, intent) => {
            if (typeof window !== 'undefined') {
              const event = new CustomEvent('navigate-with-intent', {
                detail: { section, intent }
              });
              window.dispatchEvent(event);
            }
          }} />
          {/* UI-HEADER-FIXO (I.160): sino e menu do usuário ficam na barra superior de todas as telas */}
        </div>
      </div>

      {/* Saving Indicator */}
      {isSaving && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <RefreshCw className="h-4 w-4 animate-spin" />
          Salvando...
        </div>
      )}

      {/* Dashboard Grid */}
      <DashboardGrid
        widgets={widgets}
        onReorder={reorderWidgets}
        onRemove={removeWidget}
        onResize={updateWidgetSize}
      />

      {/* Add Widget Button at Bottom */}
      {widgets.length > 0 && (
        <div className="flex justify-center pt-4">
          <Button 
            variant="outline"
            onClick={() => setIsAddDialogOpen(true)}
            className="gap-2"
          >
            <Plus className="h-4 w-4" />
            Adicionar Indicador
          </Button>
        </div>
      )}

      {/* Add Widget Dialog */}
      <AddWidgetDialog
        open={isAddDialogOpen}
        onOpenChange={setIsAddDialogOpen}
        onAddWidget={addWidget}
        currentWidgets={widgets}
      />
    </div>
  );
};
